<script setup lang="ts" generic="T extends Record<string, any>">
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { UseQueryReturnType } from '@tanstack/vue-query'
import { ButtonElement, ModalWindow, NumericInputText } from '@/shared/ui'
import { useToast } from '@/shared/toasts'
import { useAppModal } from '@/shared/composable/app-modal.js'

const { t } = useI18n()

const toast = useToast()

type LookupFieldType = 'inn' | 'pinfl'

const FIELD_CONFIG: Record<LookupFieldType, { maxLength: number }> = {
  inn: { maxLength: 9 },
  pinfl: { maxLength: 14 },
}

const props = defineProps<{
  modelValue: string
  label: string
  buttonLabel: string
  error?: string
  fieldType: LookupFieldType
  useQuery: (value: Ref<string>) => UseQueryReturnType<T, Error>
  expectedFields: { key: keyof T; label: string }[]
  minLength?: number
  confirmHeader?: string
  confirmMessage?: string
  inputDisable?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'filled', payload: T): void
  (e: 'field-error', payload: Record<string, string>): void
  (e: 'reset'): void
}>()

const isFilled = ref(false)
const { isModalOpen, openModal, closeModal } = useAppModal()

const effectiveMaxLength = computed(() => FIELD_CONFIG[props.fieldType].maxLength)

const model = computed({
  get: () => props.modelValue,
  set: (v: string) => emit('update:modelValue', v),
})

const queryValue = ref('')
const { isFetching, refetch, error: queryError } = props.useQuery(queryValue)

const modalHeader = computed(() =>
  isFilled.value
    ? (props.confirmHeader ?? t('common.confirmTitle'))
    : t('common.fillTitle'),
)

const modalMessage = computed(() =>
  isFilled.value
    ? (props.confirmMessage ?? t('common.unsavedChangesMessage'))
    : t('common.unsavedChangesMessage'),
)

const handleResult = (data: T) => {
  emit('filled', data)
  isFilled.value = true

  const fieldErrors: Record<string, string> = {}
  props.expectedFields.forEach(({ key, label }) => {
    if (!data[key]) fieldErrors[key as string] = `Не удалось заполнить поле «${label}»`
  })

  if (Object.keys(fieldErrors).length) {
    emit('field-error', fieldErrors)
    toast.error('Данные заполнены не полностью')
  } else {
    toast.success('Данные успешно заполнены')
  }
}

const performLookup = async () => {
  const expectedLength = effectiveMaxLength.value
  if (
    !model.value ||
    (props.minLength && model.value.length < props.minLength) ||
    model.value.length !== expectedLength
  ) {
    toast.error('Введите корректное значение')
    return
  }

  queryValue.value = model.value
  const result = await refetch()

  if (result.error || !result.data) {
    toast.error(result.error instanceof Error ? result.error.message : 'Не удалось получить данные')
    return
  }

  handleResult(result.data)
}

const handleButtonClick = () => {
  openModal()
}

const confirmRefill = () => {
  closeModal()
  if (isFilled.value) {
    emit('reset')
  }
  performLookup()
}
</script>

<template>
  <div>
    <div class="flex items-end gap-2">
      <NumericInputText
        class="flex-1"
        :label="label"
        v-model="model"
        :max-length="effectiveMaxLength"
        :error="error ?? queryError?.message"
        :disabled="props.inputDisable"
      />
      <ButtonElement type="button" :loading="isFetching" :disabled="isFetching" @click="handleButtonClick">
        {{ buttonLabel }}
      </ButtonElement>
    </div>

    <ModalWindow v-if="isModalOpen" @close-modal="closeModal" modal-class="!max-w-120 !min-h-min">
      <div class="flex flex-col items-center pt-5 pb-6 text-center">
        <h3 class="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">
          {{ modalHeader }}
        </h3>
        <p class="text-sm leading-relaxed text-gray-500 dark:text-gray-400">
          {{ modalMessage }}
        </p>
      </div>

      <div class="flex items-center justify-center gap-3">
        <ButtonElement variant="compact" class="w-full" @click="closeModal">
          {{ $t('common.no') }}
        </ButtonElement>
        <ButtonElement variant="primary" class="w-full" @click="confirmRefill">
          {{ $t('common.yes') }}
        </ButtonElement>
      </div>
    </ModalWindow>
  </div>
</template>
